import {
  ApiAssetsAdapterService,
  AssetFilter,
} from '@fullyops/data/assets/api-adapter';
import { MiniAsset } from '@fullyops/data/assets/asset';
import { createStore } from '@ngneat/elf';
import {
  addEntities,
  deleteAllEntities,
  selectAllEntities,
  setEntities,
  withEntities,
} from '@ngneat/elf-entities';
import { forkJoin, map, Observable, Subscription } from 'rxjs';
import { ActionTypeLabel } from '@fullyops/data/action-types/entities/action-type-entity';
import { nilUUID } from '@fullyops/shared/nil-uuid';

export class AssetsRepository {
  private store = createStore(
    { name: 'mini-assets' },
    withEntities<MiniAsset>({ idKey: 'id' }),
  );

  readonly assets$ = this.store.pipe(selectAllEntities());

  constructor(private api: ApiAssetsAdapterService) {}

  private refreshSub?: Subscription;

  refresh(filter: AssetFilter): void {
    this.replaceWith(this.api.getAllMini(filter));
  }

  /**
   * Unassigned assets on their own, for installation work orders that may only reference assets
   * nobody holds yet. The rows are tagged: the ownership is known from the call they came back on
   * rather than inferred, so the pickers can label them and sort them above anything untagged —
   * such as an asset this work order already installed, which arrives from the work order itself
   * and carries no tag.
   */
  refreshUnassigned(actionType: ActionTypeLabel): void {
    this.replaceWith(this.fetchTagged(nilUUID, actionType, 'UNASSIGNED'));
  }

  /**
   * The assets an installation work order can act on: the unassigned ones, plus those already
   * assigned to the company, which can be installed again. The unassigned ones come first on
   * purpose: installing an asset the client already has is the exception, so it belongs at the end
   * of the list. Both setEntities and the pickers preserve this order, and each call is already
   * name-ordered by the back end.
   */
  refreshInstallable(companyId: string, actionType: ActionTypeLabel): void {
    this.replaceWith(
      forkJoin([
        this.fetchTagged(nilUUID, actionType, 'UNASSIGNED'),
        this.fetchTagged(companyId, actionType, 'CLIENT_OWNED'),
      ]).pipe(map(([unassigned, assigned]) => [...unassigned, ...assigned])),
    );
  }

  add(asset: MiniAsset): void {
    this.store.update(addEntities(asset));
  }

  private fetchTagged(
    companyId: string,
    actionType: ActionTypeLabel,
    ownership: MiniAsset['ownership'],
  ): Observable<MiniAsset[]> {
    return this.api
      .getAllMini({ companyId, compatibleWithActionType: actionType })
      .pipe(map((as) => as.map((a) => ({ ...a, ownership }))));
  }

  private replaceWith(assets: Observable<MiniAsset[]>): void {
    // Ensure we never have two of these requests in flight at once; the new asset page may
    // invoke this method multiple times.
    this.refreshSub?.unsubscribe();
    this.refreshSub = assets.subscribe((as) =>
      this.store.update(setEntities(as)),
    );
  }

  clear(): void {
    this.store.update(deleteAllEntities());
  }

  destroy(): void {
    this.store.destroy();
  }
}
